import 'config/react-select.css'
import {GroupBase} from 'react-select/dist/declarations/src/types'
import {StateManagerProps} from 'react-select/dist/declarations/src/useStateManager'
import {StylesConfig} from 'react-select/dist/declarations/src/styles'
import {ReactElement, RefAttributes} from 'react'
import {CustomFieldSelectComponentCustomProps} from 'module/CustomField/component/input/CustomFieldSelectComponent'
import {Interpolation} from '@emotion/serialize'
import {Theme} from '@emotion/react'
import Select from 'react-select/dist/declarations/src/Select'

/**
 * styles from defaults have to be applied in overridden elements also,
 * because style attribute on MultiValueContainer is applied to inner element and not on container
 *
 */
export const ReactSelectMultiValueCustomizationCSS = {
	width: '100%',
	display: 'flex',
	justifyContent: 'space-between',
}

export type ReactSelectAction = 'create-option' | 'select-option' | 'deselect-option' | 'clear' | 'remove-value' | 'pop-value' | 'set-value'
export type ReactSelectChangeHandlerOptions = { action: ReactSelectAction }
export type ReactSelectOnChange<T> = (option: T | null, action: ReactSelectChangeHandlerOptions) => void
export type ReactSelectOnChangeMulti<T> = (options: T[], action: ReactSelectChangeHandlerOptions) => void

export interface ReactSelectRefType {
	inputRef: HTMLInputElement | null
}

export interface CustomReactSelectProps<
	Option = unknown,
	IsMulti extends boolean = boolean,
	Group extends GroupBase<Option> = GroupBase<Option>
> {
	ref?: React.Ref<any> // todo replace with official ref prop??
}

export type ReactSelectProps<
	Option = unknown,
	IsMulti extends boolean = boolean,
	Group extends GroupBase<Option> = GroupBase<Option>
> = StateManagerProps<Option, IsMulti, Group> & CustomReactSelectProps<Option, IsMulti, Group> & {
	css?: Interpolation<Theme>
}

export type ReactSelect<
	Option = unknown,
	IsMulti extends boolean = boolean,
	Group extends GroupBase<Option> = GroupBase<Option>
> = <
	Option = unknown,
	IsMulti extends boolean = false,
	Group extends GroupBase<Option> = GroupBase<Option>
>(
	props: ReactSelectProps<Option, IsMulti, Group> & RefAttributes<Select<Option, IsMulti, Group>>,
) => ReactElement

// todo typing does not work correctly. Record<string, any> allows any Props on React select.
export function getReactSelectDefaultProps<
	Option = unknown,
	IsMulti extends boolean = boolean,
	Group extends GroupBase<Option> = GroupBase<Option>,
	Extension extends {} = {}
>({
	styles,
	placeholder,
	...props
}: ReactSelectProps<Option, IsMulti, Group> & Extension): ReactSelectProps<Option, IsMulti, Group> & Extension {
	return {
		menuPortalTarget: document.body, // do not use scrollable content for this portal target. otherwise it would scroll away and/or selected value may be wrong! body is not longer scrolling! #PanelViewWrapContent is scrolling.
		autoFocus: false,
		hideSelectedOptions: false,
		placeholder: placeholder || "Auswählen...",
		noOptionsMessage: (() => "Keine Einträge gefunden") as ReactSelectProps<Option, IsMulti, Group>['noOptionsMessage'],
		loadingMessage: (() => "Laden...") as ReactSelectProps<Option, IsMulti, Group>['loadingMessage'],
		styles: {
			placeholder: (base) => ({
				...base,
				margin: '1px 3px 2px 3px',
			}),
			valueContainer: (base) => ({
				...base,
				padding: '0 2px',
				overflowY: 'auto',
			}),
			control: (base) => ({
				...base,
				minHeight: undefined, // fixes: height adapts to font-size
			}),
			noOptionsMessage: (base) => ({
				...base,
				whiteSpace: 'pre-line',
			}),
			option: (base) => ({
				...base,
				wordBreak: 'break-word',
				fontSize: 14,
			}),
			menuPortal: (base) => ({
				...base,
				zIndex: 999999,
				top: typeof base.top === 'number' ? +base.top - 7 : base.top,
			}),
			menu: (base) => ({
				...base,
				overflow: 'hidden',
				zIndex: 999999,
				boxShadow: '0 4px 16px hsla(0,0%,0%,0.7)',
				width: (props as CustomFieldSelectComponentCustomProps).isAze && "max-content" || '100%', //todo move out
				minWidth: '280px',
			}),
			input: (base, inputProps) => ({
				...base,
				visibility: "visible",
				color: inputProps.isDisabled ? '#363232' : '#000',
				fontSize: 13,
				paddingTop: 1,
				paddingBottom: 1,
				margin: '3px 2px',
			}),
			dropdownIndicator: base => ({...base, padding: '0px 2px'}),
			indicatorSeparator: (base) => ({...base, marginTop: 5, marginBottom: 5}),
			clearIndicator: base => ({...base, padding: '0px 2px'}),
			groupHeading: (base) => ({
				...base,
				position: 'sticky',
				background: '#ffffff',
				top: -5,
				paddingTop: 5,
				paddingBottom: 5,
			}),
			multiValue: (base) => ({
				...base,
				...ReactSelectMultiValueCustomizationCSS,
			}),
			multiValueLabel: (base) => ({
				...base,
				display: 'inline',
				fontSize: '95%',
			}),
			multiValueRemove: (base) => ({
				...base,
				background: 'inherit',
			}),
			...styles,
		} as StylesConfig<Option, IsMulti, Group>,
		...props,
	} as ReactSelectProps<Option, IsMulti, Group> & Extension
}

